import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "~/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";

const FEATURES = ["location", "odometer", "dtc", "battery"] as const;
const STEPS = [
  "open_integrations",
  "connect_fleetio",
  "enter_credentials",
  "available_vehicles",
  "connect_vehicles",
  "manage_shares",
] as const;

function Overview() {
  const { t } = useTranslation("fleetio");
  return (
    <>
      <p className="mb-4">{t("overview.paragraph_1")}</p>
      <p className="mb-4">{t("overview.paragraph_2")}</p>
    </>
  );
}

function Features() {
  const { t } = useTranslation("fleetio");
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
      {FEATURES.map((key) => (
        <div key={key} className="rounded-lg border border-[#D4D7DC] bg-white p-6">
          <h3 className="mb-3 text-lg leading-snug font-bold">{t(`features.items.${key}.title`)}</h3>
          <p className="mb-4 max-w-[355px] leading-[1.6]">{t(`features.items.${key}.body`)}</p>
          <p className="mb-1.5 text-base font-semibold">{t("features.data_types")}</p>
          <p className="leading-[1.6]">
            <strong>{t("features.bouncie_label")}</strong> {t(`features.items.${key}.bouncie`)}
          </p>
          <p className="leading-[1.6]">
            <strong>{t("features.fleetio_label")}</strong> {t(`features.items.${key}.fleetio`)}
          </p>
          <p className="leading-[1.6]">
            <strong>{t("features.includes_label")}</strong> {t(`features.items.${key}.includes`)}
          </p>
        </div>
      ))}
    </div>
  );
}

function HowToConnect() {
  const { t } = useTranslation("fleetio");
  return (
    <ol className="mx-auto flex max-w-[556px] flex-col gap-8">
      {STEPS.map((key, i) => (
        <li key={key} className="flex items-start gap-5">
          <span
            aria-hidden="true"
            className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-[#2A3141] text-xl font-bold text-white"
          >
            {i + 1}
          </span>
          <div className="flex flex-col gap-1.5">
            <h3 className="mt-[3px] mb-2.5 text-lg font-bold">{t(`steps.${key}.title`)}</h3>
            <p className="leading-[1.6]">{t(`steps.${key}.body`)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

const SECTIONS: { key: "overview" | "features" | "how_to_connect"; content: ReactNode }[] = [
  { key: "overview", content: <Overview /> },
  { key: "features", content: <Features /> },
  { key: "how_to_connect", content: <HowToConnect /> },
];

// Phones get an accordion (several panels can be open), tablets and up get tabs.
export function FleetioSections() {
  const { t } = useTranslation("fleetio");

  return (
    <section className="text-[#1E1F22]">
      <div className="wrap md:hidden">
        <Accordion type="multiple" defaultValue={["overview"]} className="flex flex-col gap-2">
          {SECTIONS.map(({ key, content }) => (
            <AccordionItem key={key} value={key} className="overflow-hidden rounded border-0 bg-[#F2F2F2]">
              <AccordionTrigger data-sid={`fleetio-section-trigger:${key}`} className="px-6 py-5 text-left text-[#1E1F22]">
                {t(`sections.${key}`)}
              </AccordionTrigger>
              <AccordionContent className="px-6 pb-6">{content}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      <Tabs defaultValue="overview" className="hidden md:block">
        <TabsList className="flex justify-center border-b-0 md:gap-12 xl:gap-20">
          {SECTIONS.map(({ key }) => (
            <TabsTrigger
              key={key}
              value={key}
              data-sid={`fleetio-tab:${key}`}
              className="-mb-px border-b-[3px] px-10 py-4 text-[15px] text-[#1E1F22] hover:border-[#1F69F2] hover:text-[#112F60] data-[state=active]:border-[#1F69F2] data-[state=active]:text-[#112F60]"
            >
              {t(`sections.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {SECTIONS.map(({ key, content }) => (
          <TabsContent key={key} value={key} className="mt-0 bg-[#F2F2F2] py-12">
            <div className="wrap">{content}</div>
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}

export default FleetioSections;
